<div class="h-full w-full  overflow-y-auto scrollbar-transparent">

  <!-- Page wrapper — prepared for multiple cards stacked vertically -->
  <div class="flex flex-col gap-5 overflow-y-auto scrollbar-transparent">

    <!-- Card: Engagements Overview -->
    <section aria-labelledby="ttor-engagements-heading" class="overflow-hidden rounded-xl border border-border bg-card">
      <!-- Card header -->
      <div class="flex items-start gap-3 border-b border-border px-5 py-4">
        <div class="mt-0.5 rounded-lg bg-primary/10 p-1.5">
          <!-- Lucide: bar-chart-2 -->
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" class="size-4 text-primary" aria-hidden="true">
            <line x1="18" x2="18" y1="20" y2="10" />
            <line x1="12" x2="12" y1="20" y2="4" />
            <line x1="6" x2="6" y1="20" y2="14" />
          </svg>
        </div>
        <div class="flex flex-col gap-0.5">
          <h2 id="ttor-engagements-heading" class="text-sm font-semibold text-foreground">
            Engagements
          </h2>
          <p class="text-xs text-secondary">
            Total, organic &amp; paid breakdown across all campaign videos
          </p>
        </div>

        <!-- Legend -->
        <div class="ml-auto flex items-center gap-4 self-center" aria-label="Colour legend">
          <div class="flex items-center gap-1.5">
            <span class="size-2 rounded-full bg-primary" aria-hidden="true"></span>
            <span class="text-xs font-medium text-secondary">Organic</span>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="size-2 rounded-full bg-amber-500" aria-hidden="true"></span>
            <span class="text-xs font-medium text-secondary">Paid</span>
          </div>
        </div>
      </div>

      <!-- Metric tiles grid — gap-px + bg-border creates seamless dividers -->
      <dl class="grid grid-cols-2 gap-px bg-border sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
        @for (metric of metrics(); track metric.key) {
        <div class="flex flex-col gap-3 bg-card px-4 py-3.5">

          <!-- Icon + label row -->
          <div class="flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" [class]="metric.iconClass"
              aria-hidden="true">
              @for (path of metric.svgPaths; track $index) {
              <path [attr.d]="path" />
              }
            </svg>
            <dt class="truncate text-xs font-medium text-secondary">
              {{ metric.label }}
            </dt>
          </div>

          <dd class="flex flex-col gap-2">
            <!-- Total value -->
            <span class="text-xl font-bold tracking-tight text-foreground"
              [attr.aria-label]="metric.label + ' total: ' + metric.total">
              {{ metric.total }}
            </span>

            <!-- Organic / Paid breakdown (omitted when no breakdown exists) -->
            @if (metric.organic !== null) {
            <div class="flex flex-col gap-1" role="group" [attr.aria-label]="metric.label + ' breakdown'">
              <!-- Organic -->
              <div class="flex items-center justify-between gap-2">
                <div class="flex items-center gap-1.5">
                  <span class="size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true"></span>
                  <span class="text-xs text-secondary">Organic</span>
                </div>
                <span class="text-xs font-semibold tabular-nums text-primary">
                  {{ metric.organic }}
                </span>
              </div>

              <!-- Paid -->
              @if (metric.paid !== null) {
              <div class="flex items-center justify-between gap-2">
                <div class="flex items-center gap-1.5">
                  <span class="size-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden="true"></span>
                  <span class="text-xs text-secondary">Paid</span>
                </div>
                <span class="text-xs font-semibold tabular-nums text-amber-500">
                  {{ metric.paid }}
                </span>
              </div>
              } @else {
              <div class="flex items-center justify-between gap-2">
                <div class="flex items-center gap-1.5">
                  <span class="size-1.5 shrink-0 rounded-full bg-border" aria-hidden="true"></span>
                  <span class="text-xs text-secondary">Paid</span>
                </div>
                <span class="text-xs text-secondary/50">—</span>
              </div>
              }
            </div>
            }
          </dd>

        </div>
        }
      </dl>
    </section>
    <!-- Card: Engagements Overview - END -->


    <!-- Card: Content Performance -->
    <section aria-labelledby="ttor-content-heading" class="overflow-hidden rounded-xl border border-border bg-card">
      <!-- Card header -->
      <div class="flex items-start gap-3 border-b border-border px-5 py-4">
        <div class="mt-0.5 rounded-lg bg-primary/10 p-1.5">
          <!-- Lucide: play-circle -->
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" class="size-4 text-primary" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <polygon points="10 8 16 12 10 16 10 8" />
          </svg>
        </div>
        <div class="flex flex-col gap-0.5">
          <h2 id="ttor-content-heading" class="text-sm font-semibold text-foreground">
            Content Performance
          </h2>
          <p class="text-xs text-secondary">
            Overview of content performance and traffic sources
          </p>
        </div>

        <!-- Legend -->
        <div class="ml-auto flex items-center gap-4 self-center" aria-label="Colour legend">
          <div class="flex items-center gap-1.5">
            <span class="size-2 rounded-full bg-primary" aria-hidden="true"></span>
            <span class="text-xs font-medium text-secondary">Organic</span>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="size-2 rounded-full bg-amber-500" aria-hidden="true"></span>
            <span class="text-xs font-medium text-secondary">Paid</span>
          </div>
        </div>
      </div>
      <!-- Card header - END -->

      <div class="grid grid-cols-2 gap-px bg-border">

        <!-- LEFT: metric tiles -->
        <article class="col-span-1 flex flex-col gap-px bg-border">

          <!-- Video Views — full-width single tile -->
          @if (contentMetrics()[0]; as metric) {
          <div class="flex flex-col gap-3 bg-card px-4 py-3.5">
            <div class="flex items-center gap-1.5">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" [class]="metric.iconClass"
                aria-hidden="true">
                @for (path of metric.svgPaths; track $index) {
                <path [attr.d]="path" />
                }
              </svg>
              <dt class="truncate text-xs font-medium text-secondary">{{ metric.label }}</dt>
            </div>
            <dd class="flex flex-col gap-2">
              <span class="text-xl font-bold tracking-tight text-foreground">{{ metric.total }}</span>
              @if (metric.organic !== null) {
              <div class="flex flex-col gap-1" role="group" [attr.aria-label]="metric.label + ' breakdown'">
                <div class="flex items-center justify-between gap-2">
                  <div class="flex items-center gap-1.5">
                    <span class="size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true"></span>
                    <span class="text-xs text-secondary">Organic</span>
                  </div>
                  <span class="text-xs font-semibold tabular-nums text-primary">{{ metric.organic }}</span>
                </div>
                @if (metric.paid !== null) {
                <div class="flex items-center justify-between gap-2">
                  <div class="flex items-center gap-1.5">
                    <span class="size-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden="true"></span>
                    <span class="text-xs text-secondary">Paid</span>
                  </div>
                  <span class="text-xs font-semibold tabular-nums text-amber-500">{{ metric.paid }}</span>
                </div>
                } @else {
                <div class="flex items-center justify-between gap-2">
                  <div class="flex items-center gap-1.5">
                    <span class="size-1.5 shrink-0 rounded-full bg-border" aria-hidden="true"></span>
                    <span class="text-xs text-secondary">Paid</span>
                  </div>
                  <span class="text-xs text-secondary/50">—</span>
                </div>
                }
              </div>
              }
            </dd>
          </div>
          }

          <!-- 2×2 grid for the remaining 4 metrics -->
          <dl class="grid grid-cols-2 gap-px bg-border">
            @for (metric of contentMetrics().slice(1); track metric.key) {
            <div class="flex flex-col gap-3 bg-card px-4 py-3.5">
              <div class="flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" [class]="metric.iconClass"
                  aria-hidden="true">
                  @for (path of metric.svgPaths; track $index) {
                  <path [attr.d]="path" />
                  }
                </svg>
                <dt class="truncate text-xs font-medium text-secondary">{{ metric.label }}</dt>
              </div>
              <dd class="flex flex-col gap-2">
                <span class="text-xl font-bold tracking-tight text-foreground">{{ metric.total }}</span>
                @if (metric.organic !== null) {
                <div class="flex flex-col gap-1" role="group" [attr.aria-label]="metric.label + ' breakdown'">
                  <div class="flex items-center justify-between gap-2">
                    <div class="flex items-center gap-1.5">
                      <span class="size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true"></span>
                      <span class="text-xs text-secondary">Organic</span>
                    </div>
                    <span class="text-xs font-semibold tabular-nums text-primary">{{ metric.organic }}</span>
                  </div>
                  @if (metric.paid !== null) {
                  <div class="flex items-center justify-between gap-2">
                    <div class="flex items-center gap-1.5">
                      <span class="size-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden="true"></span>
                      <span class="text-xs text-secondary">Paid</span>
                    </div>
                    <span class="text-xs font-semibold tabular-nums text-amber-500">{{ metric.paid }}</span>
                  </div>
                  } @else {
                  <div class="flex items-center justify-between gap-2">
                    <div class="flex items-center gap-1.5">
                      <span class="size-1.5 shrink-0 rounded-full bg-border" aria-hidden="true"></span>
                      <span class="text-xs text-secondary">Paid</span>
                    </div>
                    <span class="text-xs text-secondary/50">—</span>
                  </div>
                  }
                </div>
                }
              </dd>
            </div>
            }
          </dl>

        </article>

        <!-- RIGHT: traffic sources doughnut chart -->
        <article class="col-span-1 flex flex-col gap-3 bg-card p-5">
          <div class="flex items-center gap-1.5">
            <!-- Lucide: pie-chart -->
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 shrink-0 text-accent"
              aria-hidden="true">
              <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
              <path d="M22 12A10 10 0 0 0 12 2v10z" />
            </svg>
            <span class="text-xs font-medium text-secondary">Traffic sources</span>
          </div>
          @if (hasSourceData()) {
            <div echarts class="w-full flex-1" style="min-height: 300px" [options]="sourceChartOption()"
              [autoResize]="true" aria-label="Traffic sources doughnut chart">
            </div>
          } @else {
            <div class="flex w-full flex-1 flex-col items-center justify-center gap-3 text-center" style="min-height: 300px"
              aria-label="No traffic source data available">
              <svg viewBox="0 0 120 120" class="size-24 text-border" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="60" cy="60" r="46" stroke="currentColor" stroke-width="14" stroke-dasharray="6 4" opacity="0.6"/>
                <circle cx="60" cy="60" r="22" stroke="currentColor" stroke-width="2" stroke-dasharray="4 3" opacity="0.3"/>
              </svg>
              <div class="flex flex-col gap-1">
                <p class="text-xs font-medium text-secondary">No traffic data yet</p>
                <p class="text-[11px] text-secondary/50">Data will appear once campaign videos are published</p>
              </div>
            </div>
          }
        </article>

      </div>
    </section>
    <!-- Card: Content Performance - END -->

    <!-- Card: Audience Overview -->
    <section aria-labelledby="ttor-audience-heading" class="overflow-hidden rounded-xl border border-border bg-card">
      <!-- Card header -->
      <div class="flex items-start gap-3 border-b border-border px-5 py-4">
        <div class="mt-0.5 rounded-lg bg-primary/10 p-1.5">
          <!-- Lucide: users -->
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" class="size-4 text-primary" aria-hidden="true">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>
        <div class="flex flex-col gap-0.5">
          <h2 id="ttor-audience-heading" class="text-sm font-semibold text-foreground">
            Audience
          </h2>
          <p class="text-xs text-secondary">
            Aggregated demographic breakdown across all campaign videos
          </p>
        </div>
      </div>
      <!-- Card header - END -->

      <div class="grid grid-cols-2 gap-px bg-border">

        <!-- Row 1: Gender | Device OS -->
        <article class="col-span-1 flex flex-col gap-3 bg-card p-5">
          <div class="flex items-center gap-1.5">
            <!-- Lucide: venus (gender) -->
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 shrink-0 text-rose-500"
              aria-hidden="true">
              <circle cx="12" cy="8" r="6" />
              <line x1="12" x2="12" y1="14" y2="22" />
              <line x1="9" x2="15" y1="19" y2="19" />
            </svg>
            <span class="text-xs font-medium text-secondary">Gender</span>
          </div>
          @if (hasGenderData()) {
            <div echarts class="w-full" style="height: 260px" [options]="genderChartOption()" [autoResize]="true"
              aria-label="Gender distribution chart">
            </div>
          } @else {
            <div class="flex w-full flex-col items-center justify-center gap-3 text-center" style="height: 260px"
              aria-label="No gender data available">
              <svg viewBox="0 0 120 120" class="size-20 text-border" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="60" cy="60" r="46" stroke="currentColor" stroke-width="14" stroke-dasharray="6 4" opacity="0.6"/>
                <circle cx="60" cy="60" r="22" stroke="currentColor" stroke-width="2" stroke-dasharray="4 3" opacity="0.3"/>
              </svg>
              <div class="flex flex-col gap-1">
                <p class="text-xs font-medium text-secondary">No gender data yet</p>
                <p class="text-[11px] text-secondary/50">Awaiting audience analytics</p>
              </div>
            </div>
          }
        </article>

        <article class="col-span-1 flex flex-col gap-3 bg-card p-5">
          <div class="flex items-center gap-1.5">
            <!-- Lucide: smartphone -->
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 shrink-0 text-teal-500"
              aria-hidden="true">
              <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
              <path d="M12 18h.01" />
            </svg>
            <span class="text-xs font-medium text-secondary">Device OS</span>
          </div>
          @if (hasDeviceData()) {
            <div echarts class="w-full" style="height: 260px" [options]="deviceChartOption()" [autoResize]="true"
              aria-label="Device OS distribution chart">
            </div>
          } @else {
            <div class="flex w-full flex-col items-center justify-center gap-3 text-center" style="height: 260px"
              aria-label="No device data available">
              <svg viewBox="0 0 120 120" class="size-20 text-border" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="60" cy="60" r="46" stroke="currentColor" stroke-width="14" stroke-dasharray="6 4" opacity="0.6"/>
                <circle cx="60" cy="60" r="22" stroke="currentColor" stroke-width="2" stroke-dasharray="4 3" opacity="0.3"/>
              </svg>
              <div class="flex flex-col gap-1">
                <p class="text-xs font-medium text-secondary">No device data yet</p>
                <p class="text-[11px] text-secondary/50">Awaiting audience analytics</p>
              </div>
            </div>
          }
        </article>

        <!-- Row 2: Age | Language -->
        <article class="col-span-1 flex flex-col gap-3 bg-card p-5">
          <div class="flex items-center gap-1.5">
            <!-- Lucide: bar-chart-2 -->
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 shrink-0 text-accent"
              aria-hidden="true">
              <line x1="18" x2="18" y1="20" y2="10" />
              <line x1="12" x2="12" y1="20" y2="4" />
              <line x1="6" x2="6" y1="20" y2="14" />
            </svg>
            <span class="text-xs font-medium text-secondary">Age</span>
          </div>
          @if (hasAgeData()) {
            <div echarts class="w-full" style="height: 260px" [options]="ageChartOption()" [autoResize]="true"
              aria-label="Age distribution bar chart">
            </div>
          } @else {
            <div class="flex w-full flex-col items-center justify-center gap-3 text-center" style="height: 260px"
              aria-label="No age data available">
              <svg viewBox="0 0 120 80" class="w-32 text-border" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="4"  y="50" width="14" height="28" rx="2" opacity="0.35"/>
                <rect x="22" y="30" width="14" height="48" rx="2" opacity="0.5"/>
                <rect x="40" y="18" width="14" height="60" rx="2" opacity="0.65"/>
                <rect x="58" y="10" width="14" height="68" rx="2" opacity="0.5"/>
                <rect x="76" y="36" width="14" height="42" rx="2" opacity="0.4"/>
                <rect x="94" y="56" width="14" height="22" rx="2" opacity="0.3"/>
                <line x1="0" y1="79" x2="120" y2="79" stroke="currentColor" stroke-width="1.5" opacity="0.3"/>
              </svg>
              <div class="flex flex-col gap-1">
                <p class="text-xs font-medium text-secondary">No age data yet</p>
                <p class="text-[11px] text-secondary/50">Awaiting audience analytics</p>
              </div>
            </div>
          }
        </article>

        <article class="col-span-1 flex flex-col gap-3 bg-card p-5">
          <div class="flex items-center gap-1.5">
            <!-- Lucide: globe -->
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 shrink-0 text-sky-500"
              aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
              <path d="M2 12h20" />
            </svg>
            <span class="text-xs font-medium text-secondary">Top Language</span>
          </div>
          @if (hasLanguageData()) {
            <div echarts class="w-full" style="height: 260px" [options]="languageChartOption()" [autoResize]="true"
              aria-label="Language distribution chart">
            </div>
          } @else {
            <div class="flex w-full flex-col items-center justify-center gap-3 text-center" style="height: 260px"
              aria-label="No language data available">
              <svg viewBox="0 0 120 120" class="size-20 text-border" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="60" cy="60" r="46" stroke="currentColor" stroke-width="14" stroke-dasharray="6 4" opacity="0.6"/>
                <circle cx="60" cy="60" r="22" stroke="currentColor" stroke-width="2" stroke-dasharray="4 3" opacity="0.3"/>
              </svg>
              <div class="flex flex-col gap-1">
                <p class="text-xs font-medium text-secondary">No language data yet</p>
                <p class="text-[11px] text-secondary/50">Awaiting audience analytics</p>
              </div>
            </div>
          }
        </article>

        <!-- Row 3: Countries — full width ranked list -->
        <article class="col-span-2 flex flex-col gap-4 bg-card p-5">
          <!-- Header -->
          <div class="flex items-center gap-1.5">
            <!-- Lucide: map-pin -->
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4 shrink-0 text-amber-500"
              aria-hidden="true">
              <path
                d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span class="text-xs font-medium text-secondary">Countries &amp; Regions</span>
          </div>

          <!-- Split: map left | top-5 list right -->
          @if (hasCountriesData()) {
            <div class="grid grid-cols-2 gap-8 items-center">

              <!-- Left: choropleth world map -->
              <div class="w-full">
                @if (worldGeoJson()) {
                  <div echarts class="w-full" style="height: 280px"
                    [options]="worldMapOption()"
                    [autoResize]="true"
                    aria-label="World map showing audience countries">
                  </div>
                } @else {
                  <div class="flex h-[280px] items-center justify-center">
                    <span class="text-xs text-secondary">Loading map…</span>
                  </div>
                }
              </div>

              <!-- Right: top 5 ranked list -->
              <div class="flex flex-col gap-1">
                <p class="mb-4 text-xs font-semibold text-foreground">Top countries</p>
                <dl class="flex flex-col gap-4">
                  @for (country of topCountries(); track country.name) {
                  <div class="flex flex-col gap-1.5">
                    <div class="flex items-center justify-between gap-4">
                      <dt class="text-xs text-foreground">{{ country.name }}</dt>
                      <dd class="text-xs font-semibold tabular-nums text-primary">
                        {{ (country.percentage * 100).toFixed(2) }}%
                      </dd>
                    </div>
                    <div class="h-1.5 w-full overflow-hidden rounded-full bg-border">
                      <div class="h-full rounded-full bg-primary"
                        [style.width]="(country.percentage * 100).toFixed(2) + '%'">
                      </div>
                    </div>
                  </div>
                  }
                </dl>
              </div>

            </div>
          } @else {
            <div class="flex flex-col items-center justify-center gap-4 py-8 text-center" aria-label="No country data available">
              <svg viewBox="0 0 80 80" class="size-16 text-border" fill="none" stroke="currentColor"
                stroke-width="1.5" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="40" cy="40" r="36" stroke-dasharray="5 3" opacity="0.5"/>
                <ellipse cx="40" cy="40" rx="16" ry="36" stroke-dasharray="5 3" opacity="0.4"/>
                <line x1="4" y1="40" x2="76" y2="40" stroke-dasharray="4 3" opacity="0.3"/>
                <line x1="12" y1="22" x2="68" y2="22" stroke-dasharray="3 3" opacity="0.2"/>
                <line x1="12" y1="58" x2="68" y2="58" stroke-dasharray="3 3" opacity="0.2"/>
              </svg>
              <div class="flex flex-col gap-1">
                <p class="text-xs font-medium text-secondary">No country data yet</p>
                <p class="text-[11px] text-secondary/50">Data will appear once campaign videos are published</p>
              </div>
              <div class="mt-2 flex w-full max-w-xs flex-col gap-3">
                @for (_ of [1, 2, 3, 4, 5]; track $index) {
                  <div class="flex flex-col gap-1.5">
                    <div class="flex items-center justify-between gap-4">
                      <div class="h-2.5 w-24 animate-pulse rounded bg-border/60"></div>
                      <div class="h-2.5 w-10 animate-pulse rounded bg-border/40"></div>
                    </div>
                    <div class="h-1.5 w-full overflow-hidden rounded-full bg-border/30">
                      <div class="h-full rounded-full bg-border/50" [style.width]="(20 - $index * 3) + '%'"></div>
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </article>

      </div>
    </section>
    <!-- Card: Audience Overview - END -->
  </div>
</div>